import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types/auth.types';
import { authService } from '../services/authService';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoggingOut: false,
      login: (token, user) => {
        console.log('authStore.login: Saving auth state', { 
          hasToken: !!token, 
          role: user?.role,
          email: user?.email
        });
        
        // Update zustand state
        set({ 
          token, 
          user, 
          isAuthenticated: true 
        });
        
        // Also save to localStorage immediately (sync)
        try {
          const data = {
            state: { token, user, isAuthenticated: true, isLoggingOut: false },
            version: 0
          };
          localStorage.setItem('auth-storage', JSON.stringify(data));
          console.log('authStore.login: Saved to localStorage successfully');
        } catch (e) {
          console.error('authStore.login: Failed to save to localStorage:', e);
        }
      },
      logout: async () => {
        console.log('authStore.logout: Starting logout process');
        const state = get();
        
        // Prevent multiple logout calls
        if (state.isLoggingOut) {
          console.log('authStore.logout: Already logging out, skipping');
          return;
        }
        
        set({ isLoggingOut: true });
        
        try {
          // Call backend to invalidate token
          if (state.token) {
            await authService.logout();
          }
        } catch (error) {
          console.error('authStore.logout: Backend logout failed:', error);
          // Continue with local logout even if backend fails
        }
        
        // Clear all local storage and state
        console.log('authStore.logout: Clearing local state');
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false,
          isLoggingOut: false
        });
        
        // Clear all auth-related storage
        localStorage.removeItem('auth-storage');
        sessionStorage.clear();
        
        // Clear cookies if any
        document.cookie.split(';').forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
        
        console.log('authStore.logout: Logout complete');
      },
      updateUser: (userData) => {
        const state = get();
        if (state.user) {
          set({
            user: { ...state.user, ...userData },
          });
        }
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
